import React, { useEffect, useState } from 'react';
import { getCertAiAccuracy } from '../services/api';
import { Sparkles, TrendingUp, AlertTriangle } from 'lucide-react';

/**
 * Live "AI accuracy this month" panel powered by `cert_extraction_stats`.
 * Pulls aggregate kept/corrected counts per field and renders a header
 * with overall accuracy plus a per-field bar list.
 *
 * Stays empty until at least one engineer has saved a cert with
 * `aiOriginal` populated — won't surface fake "0%" stats on a fresh DB.
 */
const formatMonthLabel = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  // Single bucket "YYYY-MM"
  const single = raw.match(/^(\d{4})-(\d{2})$/);
  if (single) {
    const d = new Date(parseInt(single[1], 10), parseInt(single[2], 10) - 1, 1);
    return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }
  return raw;
};

const accuracyTone = (a) => {
  if (a >= 0.9) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (a >= 0.7) return 'text-blue-700 bg-blue-50 border-blue-200';
  if (a >= 0.5) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
};

const barTone = (a) => {
  if (a >= 0.9) return 'bg-emerald-500';
  if (a >= 0.7) return 'bg-blue-500';
  if (a >= 0.5) return 'bg-amber-500';
  return 'bg-red-500';
};

export const CertAiAccuracyWidget = ({ months = 1 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getCertAiAccuracy(months)
      .then(d => { if (active) setData(d); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [months]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5 animate-pulse">
        <div className="h-4 w-40 bg-slate-200 rounded mb-3" />
        <div className="h-3 w-full bg-slate-100 rounded mb-2" />
        <div className="h-3 w-3/4 bg-slate-100 rounded" />
      </div>
    );
  }

  if (!data || (data.totalKept + data.totalCorrected) === 0) {
    return (
      <div
        className="bg-slate-50 border border-slate-200 rounded-md p-5 flex items-start gap-3"
        data-testid="cert-ai-accuracy-empty"
      >
        <Sparkles className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-700">
          <div className="font-semibold text-slate-900">AI accuracy</div>
          <p className="text-slate-600">
            We&apos;ll start tracking the AI&apos;s hit-rate as soon as engineers save certificates with AI-extracted circuits. Every manual edit teaches the model — accuracy will appear here as data builds up.
          </p>
        </div>
      </div>
    );
  }

  const pct = (a) => `${Math.round(a * 100)}%`;
  const monthLbl = formatMonthLabel(data.monthLabel);
  const tone = accuracyTone(data.overallAccuracy);

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="cert-ai-accuracy-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Sparkles className="h-4 w-4 text-blue-700" />
            <h3 className="font-display uppercase tracking-wide text-sm">AI accuracy</h3>
            <span className="text-xs text-slate-500 font-normal">· {monthLbl}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            How often the AI suggested the right value before the engineer edited it.
          </p>
        </div>
        <div
          className={`px-3 py-1.5 rounded-md border text-sm font-semibold ${tone}`}
          data-testid="cert-ai-accuracy-overall"
        >
          {pct(data.overallAccuracy)} <span className="text-xs font-normal opacity-70">overall</span>
        </div>
      </div>

      <div className="space-y-2.5" data-testid="cert-ai-accuracy-fields">
        {data.fields.map(f => (
          <div key={f.field} className="space-y-1" data-testid={`cert-ai-accuracy-field-${f.field}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-slate-700">{f.label}</span>
              <span className="text-slate-500 tabular-nums">
                <span className="font-semibold text-slate-800">{pct(f.accuracy)}</span>
                <span className="text-slate-400"> · {f.kept}/{f.total} correct</span>
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barTone(f.accuracy)}`}
                style={{ width: `${Math.max(2, Math.round(f.accuracy * 100))}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500 pt-1 border-t border-slate-100">
        {data.overallAccuracy >= 0.85 ? (
          <><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /><span>Solid run — model is matching the engineer&apos;s judgment most of the time.</span></>
        ) : data.overallAccuracy >= 0.6 ? (
          <><Sparkles className="h-3.5 w-3.5 text-blue-600" /><span>Improving as you correct it — every edit feeds the next extraction.</span></>
        ) : (
          <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span>Lots of corrections — this is the early-learning phase. Keep saving and accuracy will climb.</span></>
        )}
      </div>
    </div>
  );
};

export default CertAiAccuracyWidget;
